import {Component, EventEmitter, Input, Output} from '@angular/core';
import {PrimeNgImportsModule} from '../../prime-ng-imports';
import {SDKQueryPaginatorConfig} from '../../services/query-paginator.service';
import {IBaseComponent} from '../ibase/ibase.component';
import {ETreeColDataType, ETreeColFilterType, ETreeColType, TreeColDef} from '../generic-tree/tree-col-def';
import {TreeColDefCache} from '../generic-tree/tree-col-def-cache';
import {AutoRefreshInterval} from '../../services/auto-refresh.service';
import {MenuItem, TreeNode, TreeTableNode} from 'primeng/api';
import {EStatusTagDisplayMode, StatusTagComponent} from '../status-tag/status-tag.component';
import {get} from 'lodash';
import {AutoRefreshComponent} from '../auto-refresh/auto-refresh.component';
import {TreeTableContextMenuSelectEvent} from 'primeng/treetable';
import {SdkBridgeBaseModel} from '../../../../src/app/sdk-bridge';

@Component({
    selector: 'app-generic-tree',
    templateUrl: './generic-tree.component.html',
    styleUrls: ['./generic-tree.component.scss'],
    imports: [PrimeNgImportsModule, StatusTagComponent, AutoRefreshComponent]
})
export class GenericTreeComponent<t_RowType> extends IBaseComponent {
    protected readonly get = get;
    protected readonly ETreeColType = ETreeColType;
    protected readonly ETreeColDataType = ETreeColDataType;
    protected readonly ETreeColFilterType = ETreeColFilterType;
    protected readonly EStatusTagDisplayMode = EStatusTagDisplayMode;

    // ===============================================================
    // ===============================================================

    private static _treeIDGen = 1;

    // ===============================================================
    // ===============================================================

    protected _treeID: string;
    protected _treeColIDGen = 1;

    protected _buttonsColWidth = '5rem';

    protected _searchValue?: string;
    protected _cols: TreeColDef[] = [];
    protected _selectedColumns: TreeColDef[] = [];
    protected _colsCache: Record<number, TreeColDefCache> = {};
    protected _dataCols: TreeColDef[] = [];
    protected _buttonCols: TreeColDef[] = [];
    protected searcheableFields: string[] = [];
    protected filterableFields: string[] = [];
    protected rowFilters: string[] = [];
    protected _contextMenu?: MenuItem[];

    // ===============================================================
    // ===============================================================

    @Input()
    public selection: unknown;

    @Input()
    public data: TreeNode<t_RowType | SdkBridgeBaseModel>[] = [];
    public get dataAsRowData(): TreeNode<t_RowType | SdkBridgeBaseModel>[] {
        return this.data as TreeNode<t_RowType | SdkBridgeBaseModel>[];
    }

    public get cols(): TreeColDef[] {
        return this._cols;
    }
    @Input()
    public set cols(values: TreeColDef[]) {
        this._cols = values;
        this.refreshTreeColsCache();
    }

    public get selectedColumns(): TreeColDef[] {
        return this._selectedColumns;
    }
    @Input()
    public set selectedColumns(values: TreeColDef[]) {
        this._selectedColumns = this.cols.filter((col) => values.includes(col));
    }

    public get contextMenu(): MenuItem[] | undefined {
        return this._contextMenu;
    }
    @Input()
    public set contextMenu(value: MenuItem[] | undefined) {
        this._contextMenu = value;
        this.computeButtonColumnWidth();
    }

    @Input() public entityName = 'Model';
    @Input() public dataKey = 'uuid';
    @Input() public flexHeight = false;
    @Input() public selectionMode?: 'single' | 'multiple';

    @Input() public showTopBar = true;
    @Input() public showColumnToggle = true;
    @Input() public showSearch = true;
    @Input() public showRefreshButton = true;
    @Input() public showHelpButton = false;
    @Input() public showNewButton = true;
    @Input() public newButtonCaption?: string;
    @Input() public showAutoRefresh = false;
    @Input() public autoRefreshDefaultInterval = AutoRefreshInterval.OFF;

    @Input() public updateBrowerURL = true;

    @Input() public showTreeLoading = false;
    @Input() public showTreeError = false;
    @Input() public treeLoadingStr = 'Loading';

    @Input() public defaultPaginatorConfig?: SDKQueryPaginatorConfig;

    @Output() public readyEvent = new EventEmitter<void>();
    @Output() public refreshEvent = new EventEmitter<void>();
    @Output() public helpEvent = new EventEmitter<void>();
    @Output() public newEvent = new EventEmitter<void>();
    @Output() public selectionChangeEvent = new EventEmitter<TreeNode<unknown> | TreeNode<unknown>[] | null>();
    @Output() public contextMenuSelectEvent = new EventEmitter<unknown>();

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this._treeID = 'GenericTree' + GenericTreeComponent._treeIDGen.toString();
        GenericTreeComponent._treeIDGen++;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this.refreshTreeColsCache();

        this.readyEvent?.emit();
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Refresh the table cols cache
     */
    private refreshTreeColsCache() {
        this._colsCache = {};
        this.cols.forEach((x) => {
            if (!x.__iID) {
                x.__iID = this._treeColIDGen++;
            }
            const cache = new TreeColDefCache();
            cache._init(x.__iID, this.cols, x);
            this._colsCache[x.__iID] = cache;
        });

        this.searcheableFields.splice(0);
        this.filterableFields.splice(0);

        this.cols.forEach((col) => {
            if (col.type !== ETreeColType.DATA) {
                return;
            }

            if (col.searcheable === true) this.searcheableFields.push(col.field);
            if (col.filterable === true) this.filterableFields.push(col.field);
        });

        this._dataCols.splice(0);
        this._dataCols.push(...this._cols.filter((x) => x.type === ETreeColType.DATA));

        this._buttonCols.splice(0);
        this._buttonCols.push(...this._cols.filter((x) => x.type === ETreeColType.BUTTON));

        this.computeButtonColumnWidth();

        this.selectedColumns.splice(0);
        this.selectedColumns.push(...this._cols.filter((x) => x.type === ETreeColType.DATA));
    }
    /**
     * Compute the button column width
     */
    private computeButtonColumnWidth() {
        const buttonCount = this._buttonCols.length + (this._contextMenu ? 1 : 0);
        const paddingX = 2 * 0.75; // Left + Right padding of the cell
        const buttonW = 2; // Width of a button
        const buttonGapW = 2; // Width of the gap between two button

        this._buttonsColWidth = paddingX + buttonCount * buttonW + (buttonCount - 1) * buttonGapW + 'rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * Convert the $event which is an array of date to a string with the format 'MM/DD/YYYY | MM/DD/YYYY'
     * @param $event The event emitted by the calendar (array of date)
     * @param filter The filter function to set the value
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected convertIntervalDate($event: any, filter: any) {
        if ($event?.length === 2 && $event[0] && $event[1]) {
            filter(`${$event[0]?.toLocaleDateString() || ''} | ${$event[1]?.toLocaleDateString() || ''}`);
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the selection change
     * @param selection New selection
     */
    protected selectionChange(selection: TreeTableNode<unknown> | TreeTableNode<unknown>[] | null) {
        this.selectionChangeEvent.next(selection);
    }
    /**
     * When the context menu is being show for a node
     */
    protected contextMenuSelect(data: TreeTableContextMenuSelectEvent) {
        this.selection = data.node;
        this.selectionChange(data.node);
        this.contextMenuSelectEvent.next(data.node.data!);
    }
}
